class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close(evt),
    );
    // Delegated from the drawer rather than bound on the overlay, because the
    // overlay is re-created whenever #CartDrawer's markup is swapped (the gift
    // sync, quick order list); a listener on the old one went with it, and
    // clicking outside the drawer stopped closing it.
    this.addEventListener("click", (evt) => {
      if (evt.target.id === "CartDrawer-Overlay") this.close(evt);
    });
    // A real checkout gesture — pointerdown or keyboard activation only. iOS
    // retargets a burst's click onto the checkout button (it is the form's only
    // submit control), so arming from a click would hand navigation to a
    // quantity tap.
    this.addEventListener(
      "pointerdown",
      (evt) => {
        if (evt.target.closest && evt.target.closest('[name="checkout"]')) this.armCheckout();
      },
      true,
    );
    this.addEventListener(
      "keydown",
      (evt) => {
        if (evt.key !== "Enter" && evt.key !== " ") return;
        if (evt.target.closest && evt.target.closest('[name="checkout"]')) this.armCheckout();
      },
      true,
    );
    /* +/- inside #CartDrawer-Form. iOS activates the form on the touch, before
       the button's click listener, so the gesture is cancelled here and the
       step dispatched by hand — preventDefault swallows the native click on a
       phone. iOS also drops pointerdown outright on quick successive taps
       (WebKit 232324) while still delivering the click, so what was stepped is
       counted on this element: a stamp on the button is lost the moment a
       render replaces that node, which is exactly what a burst does. */
    this.addEventListener(
      "pointerdown",
      (evt) => {
        const button = evt.target.closest && evt.target.closest(".quantity__button");
        if (!button || !button.closest("#CartDrawer-Form")) return;
        evt.preventDefault();
        this.disarmCheckout();
        /* One tap at a time. While the scrim is up the drawer is mid-update,
           and this tap is dropped rather than held: a queued burst arrives
           against quantities that have already moved under it. */
        if (this.quantityLocked()) {
          this._qtySteps = 0;
          return;
        }
        this.lockQuantity();
        this._qtySteps = (this._qtySteps || 0) + 1;
        this._qtyStepAt = Date.now();
        button.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true }));
      },
      true,
    );
    this.addEventListener(
      "click",
      (evt) => {
        const button = evt.target.closest && evt.target.closest(".quantity__button");
        if (!button || !button.closest("#CartDrawer-Form")) return;
        // Cancel every one of these, stamped or not — an uncancelled one is
        // what iOS turns into a form submission.
        evt.preventDefault();
        this.disarmCheckout();
        // The synthetic click above is the step; let it through to QuantityInput.
        if (!evt.isTrusted) return;
        const stepped = this._qtySteps > 0 && Date.now() - this._qtyStepAt < 700;
        if (!stepped) {
          /* pointerdown was dropped, or it turned this tap away because the
             scrim was up. A tap the scrim refused has to be refused here too,
             or iOS delivers the burst through the click it still fires; a tap
             whose pointerdown was lost is a real step, and takes the scrim
             the way pointerdown would have. */
          this._qtySteps = 0;
          if (this.quantityLocked()) {
            evt.stopPropagation();
            return;
          }
          this.lockQuantity();
          return;
        }
        this._qtySteps -= 1;
        evt.stopPropagation();
      },
      true,
    );
    this.setHeaderCartIconAccessibility();
  }

  /* The scrim over the drawer, raised on the tap itself rather than when the
     request starts a debounce later: in that gap a burst of +/- taps used to
     get through and queue up behind one another. It is cart.js that lowers
     it, when the update it owns has painted. */
  quantityLocked() {
    const overlay = document.getElementById("CartDrawer-LoadingOverlay");
    return !!overlay && !overlay.classList.contains("hidden");
  }

  lockQuantity() {
    const overlay = document.getElementById("CartDrawer-LoadingOverlay");
    if (!overlay) return;
    overlay.classList.remove("hidden");
    clearTimeout(this._qtyLockTimer);
    /* A tap that moves nothing — plus at the quantity rule's ceiling, where
       stepUp() leaves the input alone and no change is ever dispatched —
       never reaches cart.js, so nothing there would lower this again. Drop it
       unless a request has taken it over by then. */
    this._qtyLockTimer = setTimeout(() => {
      if (window.cartQuantityRequests > 0) return;
      overlay.classList.add("hidden");
    }, 500);
  }

  armCheckout() {
    const form = document.getElementById("CartDrawer-Form");
    const now = Date.now();
    if (form) form.dataset.checkoutOk = String(now);
    this.pointCheckoutAtCart();
    this.checkoutIntentAt = now;
  }

  /* The cart URL lives in data-cart-url, so the button carries no formaction
     to submit to until a checkout gesture puts one there. */
  pointCheckoutAtCart() {
    const button = document.getElementById("CartDrawer-Checkout");
    if (!button) return;
    const form = document.getElementById("CartDrawer-Form");
    const url = button.dataset.cartUrl || (form && form.dataset.cartUrl);
    if (!url) return;
    button.setAttribute("formmethod", "post");
    button.setAttribute("formaction", url);
  }

  disarmCheckout() {
    const form = document.getElementById("CartDrawer-Form");
    if (form) form.dataset.checkoutOk = "";
    const button = document.getElementById("CartDrawer-Checkout");
    if (button) {
      button.removeAttribute("formmethod");
      button.removeAttribute("formaction");
    }
    this.checkoutIntentAt = 0;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;
    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") ||
          this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add("overflow-hidden");
  }

  close(event) {
    this.classList.remove("active");
    removeTrapFocus();
    this.restoreFocus(event);
    document.body.classList.remove("overflow-hidden");
  }

  /*
   * Focus has to go back to the cart icon when the drawer closes, but a
   * programmatic focus() still matches :focus-visible, which left a square
   * outline on the icon after every mouse close. Paint the ring only when the
   * shopper actually closed with the keyboard: Escape, or Enter/Space on the
   * close button (a keyboard-activated click reports detail === 0).
   */
  restoreFocus(event) {
    const element = this.activeElement;
    if (!element) return;

    const viaKeyboard = event
      ? event.type === "keyup" || event.detail === 0
      : false;

    if (viaKeyboard) {
      element.focus({ preventScroll: true });
      return;
    }

    element.classList.add("no-focus-ring");
    element.addEventListener(
      "blur",
      () => element.classList.remove("no-focus-ring"),
      { once: true },
    );
    element.focus({ preventScroll: true, focusVisible: false });
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id,
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open"),
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    /* The markup is already in hand, so this is a synchronous swap with
       nothing to wait for. Raising the spinner here only flashed it over
       content that was about to appear, and on the fast add-to-cart path
       (cart.js returns early when the add response already carries the
       drawer section) nothing ever lowered it again, leaving a spinning
       overlay over the open drawer that also swallowed clicks. */
    const drawerOverlay = document.getElementById('CartDrawer-LoadingOverlay');
    if (drawerOverlay) drawerOverlay.classList.add('hidden');
    /* The cart has an item in it by definition here. Callers used to clear
       this themselves, and the ones that forgot opened a full drawer still
       wearing the empty-cart layout (cart-drawer.is-empty .drawer__inner). */
    this.classList.remove("is-empty");
    const drawerInner = this.querySelector(".drawer__inner");
    if (drawerInner) drawerInner.classList.remove("is-empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement || !parsedState.sections || !parsedState.sections[section.id]) return;
      const html = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector,
      );
      if (typeof html === "string" && sectionElement.innerHTML !== html) {
        // Through cart.js so a new gift or upsell line fades in; see writeCartRows.
        if (typeof writeCartRows === "function") writeCartRows(sectionElement, html);
        else sectionElement.innerHTML = html;
      }
      /* #cart-icon-bubble is also written by a quantity change, through
         cart.js's setInnerHTMLIfChanged, which remembers what it wrote. This
         write goes around it, so drop that record: otherwise a later change
         back to the count it remembers is skipped and the bubble keeps this
         add's count. */
      if (typeof forgetInnerHTML === "function") forgetInnerHTML(sectionElement);
    });

    // open() already defers the class that starts the slide by a task; a
    // second timeout around it only held the drawer shut for another one.
    this.open();

    if (typeof window.refreshAffirmMessaging === "function") window.refreshAffirmMessaging();
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const section = doc.querySelector(selector);
    if (section && window.transformText) window.transformText(section);
    return section.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}


/* iOS Safari submits #CartDrawer-Form on its own when a +/- button inside it
   is touched, and reports the checkout button as the submitter because that is
   the only type=submit associated with the form — so the submitter alone proves
   nothing. The form is action="#" method="dialog" and the button ships with no
   formaction, so a submit this guard misses has no /cart to request. Allow one
   only after a real checkout gesture, and only then point the button at the
   cart: the browser reads the submitter's action *after* this event, so setting
   it here is in time, and an unarmed submit never sees it.
   .submit() never fires a submit event, so it is patched too. */
if (!window.__cartDrawerSubmitGuard) {
  window.__cartDrawerSubmitGuard = true;
  const checkoutArmed = (form) => {
    const at = Number(form.dataset.checkoutOk || 0);
    return at > 0 && Date.now() - at < 1500;
  };
  window.addEventListener(
    "submit",
    (evt) => {
      const form = evt.target;
      if (!form || form.id !== "CartDrawer-Form") return;
      const submitter = evt.submitter;
      const viaCheckout = !submitter || submitter.name === "checkout";
      if (checkoutArmed(form) && viaCheckout) {
        const drawer = form.closest("cart-drawer");
        if (drawer) drawer.pointCheckoutAtCart();
        return;
      }
      evt.preventDefault();
      evt.stopPropagation();
    },
    true,
  );
  // Clear after the form's own onsubmit has read the flag. Clearing in the
  // capture phase made that handler think the tap was not checkout.
  window.addEventListener("submit", (evt) => {
    const form = evt.target;
    if (!form || form.id !== "CartDrawer-Form") return;
    form.dataset.checkoutOk = "";
  });
  const nativeSubmit = HTMLFormElement.prototype.submit;
  HTMLFormElement.prototype.submit = function () {
    if (this && this.id === "CartDrawer-Form") {
      if (!checkoutArmed(this)) return;
      this.dataset.checkoutOk = "";
    }
    return nativeSubmit.apply(this, arguments);
  };
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  // The name pills are rebuilt on redraw by CartItems.getSectionInnerHTML (cart.js).

  /* A quantity change replaces .drawer__inner, and the +/- just pressed goes
     with it. From the keyboard that dropped focus to <body>, so the next Tab
     started at the top of the page behind the drawer, and the trap still held
     the old first/last controls. Put focus back on the same button of the
     same line (the dialog, if the line is gone) and trap again around the new
     markup. Keyboard only: a tap leaves no :focus-visible button behind, and
     cart.js keeps focus() off the touch path on purpose. */
  renderSections(sectionsToRender, sections, itemCount, giftsFaded) {
    let stepped = null;
    const active = document.activeElement;
    try {
      if (active && active.matches("#CartDrawer-Form .quantity__button:focus-visible")) {
        const row = active.closest(".cart-item");
        stepped = { button: active, row: row && row.id, name: active.getAttribute("name") };
      }
    } catch (e) {
      // No :focus-visible support; leave focus alone.
    }

    // An emptied cart may hold the paint while a gift fades; this runs again then.
    super.renderSections(sectionsToRender, sections, itemCount, giftsFaded);

    if (!stepped || stepped.button.isConnected || typeof trapFocus !== "function") return;
    const drawer = document.querySelector("cart-drawer");
    if (!drawer || !drawer.classList.contains("active")) return;
    const row = stepped.row && document.getElementById(stepped.row);
    const target =
      (row && row.querySelector(`.quantity__button[name="${stepped.name}"]`)) ||
      drawer.querySelector(".drawer__inner");
    const container = drawer.classList.contains("is-empty")
      ? drawer.querySelector(".drawer__inner-empty")
      : document.getElementById("CartDrawer");
    if (target && container) trapFocus(container, target);
  }

  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
